import React, { useEffect, useState } from 'react';
import { db, collection, onSnapshot, query, where, orderBy, updateDoc, doc, handleFirestoreError, OperationType, deleteDoc } from '../firebase';
import { Notification } from '../types';
import { useAuth } from '../App';
import { Bell, X, Check, Trash2, ExternalLink } from 'lucide-react';
import { motion, AnimatePresence } from 'motion/react';
import { Link } from 'react-router-dom';
import { toast } from 'sonner';

const NotificationCenter: React.FC = () => {
  const [notifications, setNotifications] = useState<Notification[]>([]);
  const [isOpen, setIsOpen] = useState(false);
  const [filter, setFilter] = useState<'unread' | 'all'>('unread');
  const { user } = useAuth();

  useEffect(() => {
    if (!user) return;

    const q = query(
      collection(db, 'notifications'),
      where('userId', '==', user.uid),
      orderBy('createdAt', 'desc')
    );

    const unsubscribe = onSnapshot(q, (snapshot) => {
      const notifs = snapshot.docs.map(doc => ({ id: doc.id, ...doc.data() } as Notification));
      setNotifications(notifs);
    }, (error) => {
      handleFirestoreError(error, OperationType.LIST, 'notifications');
    });

    return unsubscribe;
  }, [user]);

  const markAsRead = async (id: string) => {
    try {
      await updateDoc(doc(db, 'notifications', id), { read: true });
    } catch (error) {
      handleFirestoreError(error, OperationType.UPDATE, `notifications/${id}`);
    }
  };

  const deleteNotification = async (id: string) => {
    try {
      await deleteDoc(doc(db, 'notifications', id));
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, `notifications/${id}`);
    }
  };

  const [showConfirmClear, setShowConfirmClear] = useState(false);

  const clearAllNotifications = async () => {
    if (notifications.length === 0) return;
    setShowConfirmClear(true);
  };

  const confirmClearAll = async () => {
    setShowConfirmClear(false);
    try {
      const batch = notifications.map(n => deleteDoc(doc(db, 'notifications', n.id)));
      await Promise.all(batch);
      toast.success('All notifications cleared');
    } catch (error) {
      handleFirestoreError(error, OperationType.DELETE, 'notifications');
      toast.error('Failed to clear notifications');
    }
  };

  const unreadCount = notifications.filter(n => !n.read).length;

  return (
    <div className="relative">
      <button
        onClick={() => setIsOpen(!isOpen)}
        className="relative p-2 text-white bg-white/10 hover:bg-white/20 rounded-xl transition-all md:p-2.5 cursor-pointer flex items-center justify-center"
      >
        <Bell className="w-5 h-5 md:w-6 md:h-6" />
        {unreadCount > 0 && (
          <span className="absolute -top-1 -right-1 w-4 h-4 md:w-5 md:h-5 bg-yellow-400 text-slate-900 text-[8px] md:text-[10px] font-black flex items-center justify-center rounded-full border-2 border-orange-500 shadow-lg shadow-black/25 animate-pulse">
            {unreadCount}
          </span>
        )}
      </button>

      <AnimatePresence>
        {isOpen && (
          <>
            <div
              className="fixed inset-0 z-[100]"
              onClick={() => setIsOpen(false)}
            />
            <motion.div
              initial={{ opacity: 0, y: 10, scale: 0.95 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: 10, scale: 0.95 }}
              className="absolute right-0 mt-3 w-[calc(100vw-2rem)] md:w-96 bg-white rounded-[32px] shadow-2xl border border-slate-100 z-[101] overflow-hidden"
            >
              <div className="p-6 border-b border-slate-50 flex items-center justify-between bg-blue-900">
                <h3 className="font-black text-white tracking-tight">Notifications</h3>
                <span className="text-[10px] font-black text-orange-500 bg-white px-3 py-1 rounded-full uppercase tracking-widest">
                  {unreadCount} New
                </span>
              </div>

              <div className="flex bg-slate-50 border-b border-slate-100 p-2 gap-1">
                <button
                  onClick={() => setFilter('unread')}
                  className={`flex-1 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${filter === 'unread' ? 'bg-white text-blue-900 shadow-sm font-black' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  Unread Only ({unreadCount})
                </button>
                <button
                  onClick={() => setFilter('all')}
                  className={`flex-1 py-1.5 rounded-xl text-[10px] font-black uppercase tracking-wider transition-all cursor-pointer ${filter === 'all' ? 'bg-white text-blue-900 shadow-sm font-black' : 'text-slate-400 hover:text-slate-600'}`}
                >
                  All Alerts ({notifications.length})
                </button>
              </div>

              <div className="max-h-[400px] overflow-y-auto">
                {notifications.filter(n => filter === 'all' || !n.read).length > 0 ? (
                  <div className="divide-y divide-slate-50">
                    {notifications.filter(n => filter === 'all' || !n.read).map((notification) => (
                      <div
                        key={notification.id}
                        className={`p-5 transition-colors hover:bg-slate-50 group relative ${!notification.read ? 'bg-blue-50/30' : ''}`}
                      >
                        <div className="flex gap-4">
                          <div className={`w-2.5 h-2.5 rounded-full mt-1.5 flex-shrink-0 ${!notification.read ? 'bg-orange-500 shadow-lg shadow-orange-500/40' : 'bg-transparent'}`} />
                          <div className="flex-1 min-w-0">
                            <p className="text-sm font-black text-slate-900 tracking-tight">{notification.title}</p>
                            <p className="text-xs text-slate-600 mt-1.5 leading-relaxed font-medium">{notification.message}</p>
                            <p className="text-[10px] text-slate-400 mt-3 font-bold uppercase tracking-wider">
                              {notification.createdAt?.toDate?.()?.toLocaleString() || 'Just now'}
                            </p>
                            
                            {notification.link && (
                              <Link
                                to={notification.link}
                                onClick={() => setIsOpen(false)}
                                className="inline-flex items-center gap-1.5 text-[10px] font-black text-blue-700 mt-3 hover:text-orange-500 transition-colors uppercase tracking-widest"
                              >
                                View Details <ExternalLink className="w-3 h-3" />
                              </Link>
                            )}
                          </div>
                          
                          <div className="flex flex-col gap-1 opacity-0 group-hover:opacity-100 transition-opacity">
                            {!notification.read && (
                              <button
                                onClick={() => markAsRead(notification.id)}
                                className="p-1.5 text-emerald-600 hover:bg-emerald-50 rounded-lg"
                                title="Mark as read"
                              >
                                <Check className="w-4 h-4" />
                              </button>
                            )}
                            <button
                              onClick={() => deleteNotification(notification.id)}
                              className="p-1.5 text-red-600 hover:bg-red-50 rounded-lg"
                              title="Delete"
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </div>
                      </div>
                    ))}
                  </div>
                ) : (
                  <div className="p-10 text-center">
                    <div className="w-12 h-12 bg-slate-50 rounded-2xl flex items-center justify-center mx-auto mb-4">
                      <Bell className="w-6 h-6 text-slate-300" />
                    </div>
                    <p className="text-sm text-slate-500 italic">No notifications yet</p>
                  </div>
                )}
              </div>

              {notifications.length > 0 && (
                <div className="p-3 bg-slate-50/50 border-t border-slate-50 flex items-center justify-center gap-4">
                  <button
                    onClick={() => {
                      notifications.filter(n => !n.read).forEach(n => markAsRead(n.id));
                    }}
                    className="text-[10px] font-bold text-slate-500 hover:text-slate-900 uppercase tracking-wider"
                  >
                    Mark all as read
                  </button>
                  <button
                    onClick={clearAllNotifications}
                    className="text-[10px] font-bold text-red-500 hover:text-red-700 uppercase tracking-wider"
                  >
                    Clear all
                  </button>
                </div>
              )}
            </motion.div>
          </>
        )}
      </AnimatePresence>

      <AnimatePresence>
        {showConfirmClear && (
          <div className="fixed inset-0 z-[200] flex items-center justify-center p-4">
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              exit={{ opacity: 0 }}
              onClick={() => setShowConfirmClear(false)}
              className="absolute inset-0 bg-slate-900/40 backdrop-blur-sm"
            />
            
            <motion.div
              initial={{ opacity: 0, scale: 0.95, y: 10 }}
              animate={{ opacity: 1, scale: 1, y: 0 }}
              exit={{ opacity: 0, scale: 0.95, y: 10 }}
              className="relative w-full max-w-sm bg-white rounded-3xl shadow-2xl p-6 border border-slate-100 z-10"
            >
              <div className="text-center">
                <div className="w-12 h-12 bg-red-50 text-red-500 rounded-2xl flex items-center justify-center mx-auto mb-4">
                  <Trash2 className="w-6 h-6" />
                </div>
                <h3 className="text-lg font-black text-slate-900 tracking-tight">Clear All Notifications</h3>
                <p className="text-xs text-slate-500 mt-2 leading-relaxed">
                  Are you sure you want to permanently clear all notifications? This action cannot be undone.
                </p>
              </div>

              <div className="flex gap-3 mt-6">
                <button
                  onClick={() => setShowConfirmClear(false)}
                  className="flex-1 py-3 bg-slate-100 hover:bg-slate-200 text-slate-700 rounded-2xl font-bold text-xs uppercase tracking-wider transition-all cursor-pointer"
                >
                  No
                </button>
                <button
                  onClick={confirmClearAll}
                  className="flex-1 py-3 bg-red-600 hover:bg-red-700 text-white rounded-2xl font-bold text-xs uppercase tracking-wider transition-all shadow-md shadow-red-200 cursor-pointer"
                >
                  Yes
                </button>
              </div>
            </motion.div>
          </div>
        )}
      </AnimatePresence>
    </div>
  );
};

export default NotificationCenter;
